import { createBaseApi } from '../baseApi';
import { validateRentalPrices } from '../../../utils/rentalPriceUtils';
import { BACKEND_URL } from '../../../utils/config';

const isV5 = BACKEND_URL.endsWith('/api');

const listingApi = createBaseApi('listingApi');

// Helper function to handle validation errors
const transformErrorResponse = (response) => {
  // Try to extract structured errors from response
  if (response.data && response.data.data && response.data.data.errors) {
    return { errors: response.data.data.errors };
  } else if (response.data && response.data.errors) {
    return { errors: response.data.errors };
  }
  return response;
};

export const enhancedListingApi = listingApi.injectEndpoints({
  endpoints: (builder) => ({

    // Product queries
    getProduct: builder.query({
      query: (id) => ({
        url: `/products/${id}`,
        params: isV5 ? { populate: '*' } : undefined,
        credentials: 'omit',  // Don't send cookies or authorization headers
        headers: {
          // Explicitly clear Authorization header for this endpoint
          Authorization: ''
        }
      }),
      transformResponse: (response) => {
        // Unwrap Strapi v5 { data, meta } envelope
        const product = response?.data || response;

        // Fix relative photo URLs
        if (product.photos) {
          product.photos = product.photos.map(photo => ({
            ...photo,
            url: photo.url?.startsWith('http') ? photo.url : `${BACKEND_URL.replace('/api', '')}${photo.url}`,
            formats: {
              ...photo.formats,
              thumbnail: photo.formats?.thumbnail ? {
                ...photo.formats.thumbnail,
                url: photo.formats.thumbnail.url?.startsWith('http')
                  ? photo.formats.thumbnail.url
                  : `${BACKEND_URL.replace('/api', '')}${photo.formats.thumbnail.url}`
              } : null
            }
          }));
        }

        return product;
      },
      providesTags: (result, error, id) => [{ type: 'Product', id }]
    }),

    // Product mutations
    createProduct: builder.mutation({
      query: (productData) => {
        // Validate rental prices before sending to backend
        if (productData.rentPriceAndTime) {
          const validation = validateRentalPrices(productData.rentPriceAndTime, 3);
          if (!validation.isValid) {
            throw new Error(validation.message);
          }
        }

        return {
          url: '/products',
          method: 'POST',
          body: {
            ...productData,
            // Always ensure category has a value as a fallback for Strapi validation
            category: productData.category || 'other'
          }
        };
      },
      transformErrorResponse,
      invalidatesTags: (result, error, _arg) => error ? [] : [{ type: 'Product', id: 'LIST' }]
    }),
    updateProduct: builder.mutation({
      query: ({ id, data }) => {
        // Validate rental prices before sending to backend
        if (data.rentPriceAndTime) {
          const validation = validateRentalPrices(data.rentPriceAndTime, 3);
          if (!validation.isValid) {
            throw new Error(validation.message);
          }
        }

        return {
          url: `/products/${id}`,
          method: 'PUT',
          body: {
            ...data,
            // Always ensure category has a value as a fallback for Strapi validation
            category: data.category || 'other'
          }
        };
      },
      transformErrorResponse,
      invalidatesTags: (result, error, { id }) => error ? [] : [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' }
      ]
    }),
    deleteProduct: builder.mutation({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' }
      ]
    }),

    // Bulk operations
    bulkDeleteProducts: builder.mutation({
      query: (ids) => {
        // Ensure ids is an array even if a single id is passed
        const idsArray = Array.isArray(ids) ? ids : [ids];

        return {
          url: '/products/bulk-delete',
          method: 'POST',
          body: { ids: idsArray }
        };
      },
      invalidatesTags: (result, error, ids) => {
        const idList = Array.isArray(ids) ? ids : [ids];
        return [
          { type: 'Product', id: 'LIST' },
          ...idList.map(id => ({ type: 'Product', id })),
          { type: 'Listing', id: 'LIST' },
          ...idList.map(id => ({ type: 'Listing', id }))
        ];
      }
    }),
    bulkToggleActivation: builder.mutation({
      query: ({ ids, active }) => ({
        url: '/products/bulk-toggle-activation',
        method: 'POST',
        body: { ids, active }
      }),
      invalidatesTags: (result, error, { ids }) => [
        { type: 'Product', id: 'LIST' },
        ...ids.map(id => ({ type: 'Product', id }))
      ]
    }),
    toggleProductActivation: builder.mutation({
      query: ({ productId, active }) => ({
        url: `/products/${productId}/toggle-activation`,
        method: 'POST',
        body: { active }
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: 'Product', id: productId },
        { type: 'Product', id: 'LIST' }
      ]
    }),

    // Listing queries (Beta)
    getListing: builder.query({
      query: (id) => ({
        url: `/listings/${id}`,
        params: isV5 ? { populate: '*' } : undefined,
        // Removed omit/clear auth to ensure proper access
      }),
      providesTags: (result, error, id) => [{ type: 'Listing', id }]
    }),

    getListingLikeStatus: builder.query({
      query: (id) => ({
        url: `/listings/${id}/like-status`,
        // Needs auth to know IF I liked it
      }),
      providesTags: (result, error, id) => [
        { type: 'Listing', id },
        { type: 'User', id: 'CURRENT' }
      ]
    }),

    getListings: builder.query({
      query: (params) => ({
        url: '/listings',
        params: isV5 ? { ...params, populate: '*' } : params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Listing', id })),
            { type: 'Listing', id: 'LIST' },
          ]
          : [{ type: 'Listing', id: 'LIST' }],
    }),

    getFavoriteListings: builder.query({
      query: () => ({
        url: '/listings/favorites',
        params: isV5 ? { populate: '*' } : undefined,
        // Needs auth
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Listing', id })),
            { type: 'User', id: 'FAVORITES' }
          ]
          : [{ type: 'User', id: 'FAVORITES' }]
    }),

    getMerchantListings: builder.query({
      query: (userId) => ({
        url: `/listings?users_permissions_user=${userId}&_limit=-1&_sort=created_at:DESC${isV5 ? '&populate=*' : ''}`,
        // Removed omit/clear auth to be safe
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Listing', id })),
            { type: 'Listing', id: 'LIST' }
          ]
          : [{ type: 'Listing', id: 'LIST' }]
    }),

    createListing: builder.mutation({
      query: (listingData) => ({
        url: '/listings',
        method: 'POST',
        body: listingData
      }),
      transformErrorResponse,
      invalidatesTags: (result, error, _arg) => error ? [] : [{ type: 'Listing', id: 'LIST' }]
    }),

    updateListing: builder.mutation({
      query: ({ id, data }) => ({
        url: `/listings/${id}`,
        method: 'PUT',
        body: data
      }),
      transformErrorResponse,
      invalidatesTags: (result, error, { id }) => error ? [] : [
        { type: 'Listing', id },
        { type: 'Listing', id: 'LIST' }
      ]
    }),

    deleteListing: builder.mutation({
      query: (id) => ({
        url: `/listings/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Listing', id },
        { type: 'Listing', id: 'LIST' }
      ]
    }),

    toggleListingLike: builder.mutation({
      query: ({ id }) => ({
        url: `/listings/${id}/like`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Listing', id },
        { type: 'User', id: 'CURRENT' },
        { type: 'User', id: 'FAVORITES' }
      ]
    }),

    incrementListingView: builder.mutation({
      query: (id) => ({
        url: `/listings/${id}/view`,
        method: 'PUT'
      }),
      // No cache invalidation — fire and forget
    }),

    incrementProductView: builder.mutation({
      query: (id) => ({
        url: `/products/${id}/view`,
        method: 'PUT'
      }),
      // No cache invalidation — fire and forget
    }),

  })
});

export const {
  // Product queries
  useGetProductQuery,
  // Product mutations
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  // Bulk operations
  useBulkDeleteProductsMutation,
  useBulkToggleActivationMutation,
  useToggleProductActivationMutation,

  // Listing hooks (Beta)
  useGetListingQuery,
  useGetListingsQuery,
  useGetFavoriteListingsQuery,
  useGetMerchantListingsQuery,
  useCreateListingMutation,
  useUpdateListingMutation,
  useDeleteListingMutation,
  useToggleListingLikeMutation,
  useGetListingLikeStatusQuery,
  useIncrementListingViewMutation,
  useIncrementProductViewMutation,
} = enhancedListingApi;
